import React, { FC } from 'react'
import Button from '@mortgage-pos/ui/components/Button'
import Tooltip from '@mortgage-pos/ui/components/Tooltip'
import { Rate } from '@mortgage-pos/types'
import { rateCodesMap } from '@mortgage-pos/data'
import { extractFeeByDescription } from '@mortgage-pos/utils'
import { RATE_SUPERLATIVES } from '@mortgage-pos/ui/data/rateSuperlatives'

export enum RateCardVariant {
  Simple,
  Recommendation,
  ReviewPage,
}

interface Props {
  rate: Rate
  handleFeeBreakdownClick?: () => void
  handleLockRateClick?: (rate: Rate) => void
  variant: RateCardVariant
  rateType?: string
  submitButtonText?: string
  showSuperlativeDescription?: boolean
}

const RateCardMasonry: FC<Props> = ({
  rate,
  handleFeeBreakdownClick,
  handleLockRateClick,
  variant = RateCardVariant.Simple,
  rateType = 'default',
  submitButtonText = 'Explore this rate',
  showSuperlativeDescription = false,
}) => {
  const { format: formatNumber } = Intl.NumberFormat('en-US')
  const matchedRateIcon = RATE_SUPERLATIVES[rateType]?.icon
  const matchedRateHeading = RATE_SUPERLATIVES[rateType]?.heading
  const matchedRateDescription = RATE_SUPERLATIVES[rateType]?.description

  const lenderCredit = extractFeeByDescription(
    rate?.loanEstimateSections?.['J']?.fees ?? [],
    'Lender Credit'
  )

  const pointsDollarAmount = formatNumber(
    Math.round(rate.loanAmount * ((rate.discountPoints ?? 0) / 100))
  )

  const loanAmountDollars = formatNumber(Math.round(rate.loanAmount))

  return (
    <section
      className={`rate-card-masonry ${
        variant === RateCardVariant.Simple ? '--simple' : ''
      }`}
      data-testid="rate-card-masonry"
    >
      {variant === RateCardVariant.Recommendation ? (
        <div className="rate-card-masonry__superlatives-container">
          <img src={matchedRateIcon} alt={`${matchedRateHeading} Icon`} />
          <span className="rate-card-masonry__superlatives-text">
            {matchedRateHeading}
          </span>
        </div>
      ) : null}

      <div className="rate-card-masonry__monthly-container">
        <span className="rate-card-masonry__monthly-amount">
          ${formatNumber(Math.round(rate.principleAndInterestPayment))}
        </span>

        <span> /mo.</span>
      </div>

      <div className="rate-card-masonry__term">{rateCodesMap[rate.term]}</div>

      <div className="rate-card-masonry__details-container">
        <div className="rate-card-masonry__details">
          <span>Rate</span>
          <span className="rate-card-masonry__details-value">{rate.rate}%</span>
        </div>

        <div className="rate-card-masonry__details">
          <span>APR</span>
          <span className="rate-card-masonry__details-value">{rate.apr}%</span>
        </div>

        {rate.discountPoints ? (
          <div className="rate-card-masonry__details">
            <span>
              Points
              <Tooltip
                text={`${rate.discountPoints} points = ${rate.discountPoints}% x $${loanAmountDollars}. These points will cost $${pointsDollarAmount} in upfront payment to reduce the rate.`}
                className="rate-card-masonry__points-tooltip"
              />
            </span>
            <span className="rate-card-masonry__details-value">
              {rate.discountPoints}
            </span>
          </div>
        ) : (
          <div className="rate-card-masonry__details">
            <span>Credits</span>
            <span className="rate-card-masonry__details-value">
              -${formatNumber(Math.round(Math.abs(lenderCredit)))}
            </span>
          </div>
        )}
      </div>

      {variant === RateCardVariant.ReviewPage ? null : (
        <>
          {showSuperlativeDescription && matchedRateDescription && (
            <div className="rate-card-masonry__description">
              <p className="rate-card-masonry__description__header">
                Why this is a match
              </p>
              <p>{matchedRateDescription}</p>
            </div>
          )}
          <div className="rate-card-masonry__lock-button-wrapper">
            <Button
              className="button button--dark rate-card-masonry__lock-button"
              content={submitButtonText}
              data-testid="rate-lock-button"
              onClick={() => handleLockRateClick(rate)}
            />
          </div>

          <div
            onClick={handleFeeBreakdownClick}
            className="rate-card-masonry__fee-breakdown"
          >
            Fee breakdown
          </div>
        </>
      )}
    </section>
  )
}

export default RateCardMasonry
